import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Send, Users } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { humanizeError } from '../utils/errorHandling';
import { getApiBase } from '../utils/apiBase';

const API = getApiBase();

const sectionCard =
  'rounded-2xl border border-[#e8e2d8] bg-[#fffcf7]/90 shadow-sm';
const iconWrap = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#c9a961]/15 text-[#8b7355]';

const FamilySpaces = () => {
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [selectedSpaceId, setSelectedSpaceId] = useState('');
  const [members, setMembers] = useState([]);
  const [loadingSpaces, setLoadingSpaces] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [newSpaceName, setNewSpaceName] = useState('');
  const [creatingSpace, setCreatingSpace] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  const selectedSpace = useMemo(
    () => spaces.find((space) => space.id === selectedSpaceId) || null,
    [spaces, selectedSpaceId]
  );

  const fetchSpaces = useCallback(async () => {
    setLoadingSpaces(true);
    try {
      const response = await axios.get(`${API}/family-spaces`);
      setSpaces(response.data);
      setSelectedSpaceId((previous) => previous || response.data[0]?.id || '');
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setLoadingSpaces(false);
    }
  }, []);

  const fetchMembers = useCallback(async (familySpaceId) => {
    if (!familySpaceId) {
      setMembers([]);
      return;
    }
    setLoadingMembers(true);
    try {
      const response = await axios.get(`${API}/family-spaces/${familySpaceId}/members`);
      setMembers(response.data);
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    fetchMembers(selectedSpaceId);
  }, [selectedSpaceId, fetchMembers]);

  const handleCreateSpace = async (event) => {
    event.preventDefault();
    if (!newSpaceName.trim()) return;
    setCreatingSpace(true);
    try {
      const response = await axios.post(`${API}/family-spaces`, { name: newSpaceName.trim() });
      toast.success('Family space created');
      setNewSpaceName('');
      await fetchSpaces();
      setSelectedSpaceId(response.data.id);
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setCreatingSpace(false);
    }
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!selectedSpaceId || !inviteEmail.trim()) return;
    setInviting(true);
    const emailTrim = inviteEmail.trim();
    try {
      const inv = await axios.post(`${API}/family-spaces/${selectedSpaceId}/invite`, {
        email: emailTrim,
        role: inviteRole,
      });
      const invitationId = inv.data?.id;
      if (invitationId) {
        try {
          const send = await axios.post(`${API}/family-spaces/invitations/${invitationId}/send-email`, {
            recipient_email: emailTrim,
          });
          const msg =
            send.data?.message ||
            (send.status === 202
              ? `Invitation email queued for ${emailTrim} — check your inbox shortly.`
              : 'Invitation email sent.');
          toast.success(msg);
        } catch (sendErr) {
          const detail = sendErr.response?.data?.detail;
          toast.error(
            typeof detail === 'string'
              ? detail
              : humanizeError(sendErr) ||
                  'Invitation was saved but the email could not be queued. Check RESEND_API_KEY, Redis, and the worker.',
          );
          toast.info('Invite saved — you can retry email from here after fixing the server mail queue.', {
            duration: 6000,
          });
        }
      } else {
        toast.success('Invitation created');
      }
      setInviteEmail('');
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setInviting(false);
    }
  };

  const spaceRowClass = (isSelected) =>
    [
      'w-full rounded-xl border px-3 py-2.5 text-left transition',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a961]/35 focus-visible:ring-offset-2 focus-visible:ring-offset-[#faf8f5]',
      isSelected
        ? 'border-[#c9a961]/45 bg-[#faf8f5] shadow-sm ring-1 ring-[#c9a961]/22'
        : 'border-[#e8e2d8] bg-[#fffcf7]/70 hover:border-[#c9a961]/35',
    ].join(' ');

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <nav className="border-b border-[#e8e2d8]/90 bg-[#faf8f5]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="shrink-0 text-[#5c534a] hover:bg-[#ebe6dc]/60 hover:text-[#1c1917]"
              data-testid="family-spaces-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-serif text-xl font-medium tracking-tight text-[#1c1917] sm:text-2xl">Family Spaces</h1>
              <p className="truncate text-xs text-[#68645e] sm:text-sm">
                Spaces, invites, and membership for your family
              </p>
            </div>
          </div>
          <Link to="/dashboard" className="hidden sm:block">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-[#c9a961]/40 bg-transparent text-[#1c1917] hover:bg-[#fffcf7]"
              data-testid="family-spaces-dashboard-link"
            >
              Dashboard
            </Button>
          </Link>
        </div>
      </nav>

      <div className="mx-auto max-w-5xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <Card className={sectionCard}>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <Plus className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Create family space</CardTitle>
                <CardDescription className="text-[#5c534a]">Add a private collaboration space for relatives.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <form className="flex flex-col gap-2 sm:flex-row sm:items-center" onSubmit={handleCreateSpace}>
              <Input
                value={newSpaceName}
                onChange={(e) => setNewSpaceName(e.target.value)}
                placeholder="e.g., Johnson Family Legacy"
                className="border-[#e8e2d8] bg-white/90 sm:min-w-0 sm:flex-1"
                data-testid="family-space-name-input"
              />
              <Button
                type="submit"
                disabled={creatingSpace || !newSpaceName.trim()}
                className="shrink-0 rounded-full bg-[#1c1917] px-6 text-[#faf7f2] hover:bg-[#2c2824] sm:w-auto"
                data-testid="create-family-space-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                {creatingSpace ? 'Creating…' : 'Create'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className={sectionCard}>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <Users className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">My family spaces</CardTitle>
                <CardDescription className="text-[#5c534a]">Choose a space to manage invites and members.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loadingSpaces ? (
              <p className="text-sm text-[#68645e]">Loading family spaces…</p>
            ) : spaces.length === 0 ? (
              <p className="text-sm text-[#68645e]">Family spaces you create or join will appear here.</p>
            ) : (
              <div className="space-y-2">
                {spaces.map((space) => (
                  <button
                    key={space.id}
                    type="button"
                    onClick={() => setSelectedSpaceId(space.id)}
                    className={spaceRowClass(selectedSpaceId === space.id)}
                    data-testid={`family-space-row-${space.id}`}
                  >
                    <div className="font-medium text-[#1c1917]">{space.name}</div>
                    <div className="text-xs text-[#68645e]">
                      Role: {space.membership_role} · Members: {space.member_count}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={sectionCard}>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <Send className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Invite relative</CardTitle>
                <CardDescription className="text-[#5c534a]">
                  {selectedSpace ? `Invite someone to “${selectedSpace.name}”` : 'Select a family space first'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <form className="grid gap-2 sm:grid-cols-[1fr_140px_auto]" onSubmit={handleInvite}>
              <div className="space-y-1">
                <Label htmlFor="invite-email" className="text-[#3d3833]">
                  Email
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="relative@example.com"
                  disabled={!selectedSpace}
                  className="border-[#e8e2d8] bg-white/90"
                  data-testid="family-space-invite-email-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="invite-role" className="text-[#3d3833]">
                  Role
                </Label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  disabled={!selectedSpace}
                  className="h-10 w-full rounded-md border border-[#e8e2d8] bg-white/90 px-3 text-sm text-[#1c1917] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a961]/35"
                  data-testid="family-space-invite-role-select"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="owner">Full access</option>
                </select>
              </div>
              <div className="self-end sm:col-span-1">
                <Button
                  type="submit"
                  disabled={!selectedSpace || inviting || !inviteEmail.trim()}
                  className="w-full rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824] sm:w-auto"
                  data-testid="family-space-invite-submit-btn"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {inviting ? 'Inviting…' : 'Invite'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className={sectionCard}>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <Users className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Members</CardTitle>
                <CardDescription className="text-[#5c534a]">
                  {selectedSpace
                    ? `Active memberships in “${selectedSpace.name}”`
                    : 'Select a family space to view members'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loadingMembers ? (
              <p className="text-sm text-[#68645e]">Loading members…</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-[#68645e]">No members found.</p>
            ) : (
              <ul className="space-y-2">
                {members.map((member) => (
                  <li
                    key={member.id}
                    className="flex items-center justify-between rounded-xl border border-[#e8e2d8] bg-[#faf8f5]/70 px-3 py-2.5"
                  >
                    <div>
                      <div className="font-medium text-[#1c1917]">{member.user_name || member.user_email || member.user_id}</div>
                      <div className="text-xs text-[#68645e]">{member.user_email || member.user_id}</div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-[#68645e]">
                      <Users className="h-3.5 w-3.5 text-[#8b7355]" aria-hidden />
                      {member.role} ({member.status})
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FamilySpaces;
